/** v3
 * <s-fitment-finder>: Year → Make → Model → Engine finder over the shop's verified Vehicle
 * entries (metaobject type `vehicle`, linked to products through the custom.vehicles metafield).
 *
 * How the stored data is read:
 * - Model names can carry an application note, e.g. "Mustang (disc-brake spindle)". The Model
 *   list shows the model ("Mustang"); the note stays on the product as a fitment detail.
 * - An entry for the plain model ("Mustang") covers every trim ("Mustang GT") for its years.
 * - Engine text "Not specified — confirm application" means the entry fits every engine.
 *   Only engines actually recorded show in the Engine list; an engine-specific entry only
 *   matches when that engine is picked.
 *
 * Order: Make unlocks after Year, Model after Make, Engine after Model. Changing an earlier
 * choice rebuilds the later lists and clears anything that no longer fits. Options are rebuilt
 * rather than hidden, because mobile Safari ignores hidden <option>s. Find parts opens the
 * all-products collection filtered to every matching Vehicle entry, so only products with
 * confirmed fitment for that vehicle come back. No message shows until a search is submitted.
 */
const UNSPECIFIED_ENGINE = /^not specified/i;

function normalizeVehicle(raw) {
  if (!raw || !raw.id) return null;
  const modelText = String(raw.model || '').trim();
  const split = modelText.match(/^(.*?)\s*\((.+)\)\s*$/);
  const engineText = String(raw.engine || '').trim();
  return {
    id: raw.id,
    make: String(raw.make || '').trim(),
    model: (split ? split[1] : modelText).trim(),
    setup: split ? split[2].trim() : '',
    engine: UNSPECIFIED_ENGINE.test(engineText) ? '' : engineText,
    start: Number(raw.start) || 0,
    end: Number(raw.end) || 0,
  };
}

const byText = (a, b) => a.localeCompare(b, undefined, { numeric: true });

class FitmentFinder extends HTMLElement {
  static NOT_LISTED = "__not_listed__";

  connectedCallback() {
    // Rear end / strut / spindle picker: each option is a collection URL, so submit just opens it.
    const applicationForm = this.querySelector('[data-fitment-application-form]');
    if (applicationForm) {
      applicationForm.addEventListener('submit', (event) => {
        event.preventDefault();
        const target = applicationForm.querySelector('[data-fitment-application]').value;
        if (target) window.location.assign(target);
      });
    }
    const keywordForm = this.querySelector('[data-fitment-keyword-form]');
    if (keywordForm) {
      this.setupKeywordMode(keywordForm);
      return;
    }
    const data = this.querySelector('[data-fitment-vehicles]');
    this.form = this.querySelector('[data-fitment-form]');
    if (!data || !this.form) return;
    this.vehicles = [];
    this.known = new Set();
    // The built-in snapshot makes the lists complete straight away; the inline list adds any
    // newer vehicles among the first 250, and the live list (below) replaces both when it loads.
    // Factory engine options (Mustang, Dodge, Chevrolet) that fill the Engine list.
    this.engineCatalog = [];
    try {
      const catalog = this.querySelector('[data-fitment-engines]');
      if (catalog) this.engineCatalog = JSON.parse(catalog.textContent);
    } catch {
      this.engineCatalog = [];
    }
    const snapshot = this.querySelector('[data-fitment-vehicles-snapshot]');
    try {
      if (snapshot) this.addVehicles(JSON.parse(snapshot.textContent));
    } catch {
      // Snapshot unreadable: carry on with the inline and live lists.
    }
    try {
      this.addVehicles(JSON.parse(data.textContent));
    } catch {
      if (!this.vehicles.length) return;
    }
    this.year = this.querySelector('[data-fitment-year]');
    this.make = this.querySelector('[data-fitment-make]');
    this.model = this.querySelector('[data-fitment-model]');
    this.engine = this.querySelector('[data-fitment-engine]');
    this.status = this.querySelector('[data-fitment-status]');

    this.onChange = this.handleChange.bind(this);
    this.onSubmit = this.handleSubmit.bind(this);
    this.form.addEventListener('change', this.onChange);
    this.form.addEventListener('submit', this.onSubmit);
    this.form.reset();
    this.rebuild();
    this.loadRemainingVehicles();
  }

  addVehicles(list) {
    let added = 0;
    (Array.isArray(list) ? list : []).forEach((raw) => {
      const v = normalizeVehicle(raw);
      if (!v || !v.make || !v.model || !v.start || this.known.has(v.id)) return;
      this.known.add(v.id);
      this.vehicles.push(v);
      added += 1;
    });
    return added;
  }

  /**
   * The block renders up to 250 vehicles inline. The storefront can't page the vehicle list
   * itself, so read every page of the fitment-vehicles data view (vehicles taken from each
   * product's fitment) and merge them in, keeping whatever the shopper already picked.
   */
  async loadRemainingVehicles() {
    if (!this.dataset.vehicleDataUrl) return;
    const current = new URLSearchParams(window.location.search);
    const fetchPage = (page) => {
      const url = new URL(this.dataset.vehicleDataUrl, window.location.origin);
      // Keep theme-preview parameters so a preview reads its own theme's data view.
      ['preview_theme_id', '_ab', '_fd', '_sc'].forEach((key) => {
        if (current.has(key)) url.searchParams.set(key, current.get(key));
      });
      url.searchParams.set('view', 'fitment-vehicles');
      url.searchParams.set('page', String(page));
      return fetch(url.toString(), { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : {}))
        .catch(() => ({}));
    };
    const first = await fetchPage(1);
    const pages = Math.min(Number(first.pages || 1), 20);
    const rest = [];
    for (let page = 2; page <= pages; page += 1) rest.push(fetchPage(page));
    const results = [first, ...(await Promise.all(rest))];
    const complete = results.every((data) => Array.isArray(data.vehicles));
    const live = results.flatMap((data) => (Array.isArray(data.vehicles) ? data.vehicles : []));
    if (!live.length) return;
    if (complete) {
      // Every page loaded: the live list is the source of truth, so vehicles removed since the
      // snapshot drop out and new ones appear.
      this.vehicles = [];
      this.known = new Set();
    }
    this.addVehicles(live);
    this.rebuild();
  }

  /**
   * Does entry v cover the chosen model? An exact match always does. An entry for the plain
   * model (e.g. "Mustang", no trim) also covers every trim of it ("Mustang GT", "Mustang EcoBoost")
   * for those years, because the product's fitment names the model without limiting the trim.
   * A trim-specific entry never covers the plain model or other trims.
   */
  coversModel(v, model) {
    return v.model === model || model.startsWith(`${v.model} `);
  }

  /**
   * Does entry v cover the chosen engine? Entries with no engine on record fit every engine.
   * An engine-specific entry only matches when the shopper picks that engine, so a part
   * verified for one engine never shows for another. NOT_LISTED = "engine not listed".
   */
  coversEngine(v, engine) {
    if (!v.engine) return true;
    return Boolean(engine) && engine !== FitmentFinder.NOT_LISTED && v.engine === engine;
  }

  /** Vehicles that fit the current choices; pass a key as '' to ignore that choice. */
  matches({ year = this.year.value, make = this.make.value, model = this.model.value } = {}) {
    return this.vehicles.filter((v) => {
      if (year && (Number(year) < v.start || Number(year) > v.end)) return false;
      if (make && v.make !== make) return false;
      if (model && !this.coversModel(v, model)) return false;
      return true;
    });
  }

  fill(select, options, enabled) {
    const current = select.value;
    while (select.options.length > 1) select.remove(1);
    options.forEach(({ value, label }) => select.add(new Option(label, value)));
    select.value = options.some((o) => o.value === current) ? current : '';
    select.disabled = !enabled || options.length === 0;
  }

  rebuild() {
    const years = new Set();
    this.vehicles.forEach((v) => {
      for (let y = v.end; y >= v.start && y > 0; y -= 1) years.add(y);
    });
    this.fill(this.year, [...years].sort((a, b) => b - a).map((y) => ({ value: String(y), label: String(y) })), true);

    const hasYear = Boolean(this.year.value);
    const makes = hasYear ? [...new Set(this.matches({ make: '', model: '' }).map((v) => v.make))].sort(byText) : [];
    this.fill(this.make, makes.map((m) => ({ value: m, label: m })), hasYear);

    const hasMake = hasYear && Boolean(this.make.value);
    const models = hasMake ? [...new Set(this.matches({ model: '' }).map((v) => v.model))].sort(byText) : [];
    this.fill(this.model, models.map((m) => ({ value: m, label: m })), hasMake);

    // Engine: only real engines recorded for this year / make / model. When there are none,
    // the list stays locked on "All engines" because every matching part fits any engine.
    const hasModel = hasMake && Boolean(this.model.value);
    const engines = hasModel ? this.engineChoices() : [];
    const options = engines.map((e) => ({ value: e, label: e }));
    if (options.length) options.push({ value: FitmentFinder.NOT_LISTED, label: this.dataset.engineNotListed || 'Engine not listed' });
    this.fill(this.engine, options, hasModel);
    this.engine.required = options.length > 0;
    this.engine.options[0].textContent = options.length
      ? this.dataset.selectEngine || 'Select engine'
      : this.dataset.allEngines || 'All engines';
  }

  /**
   * Engine choices for the selected year / make / model: the factory engine options when the
   * catalog lists this model and year, otherwise the engines recorded in the fitment data.
   */
  engineChoices() {
    const year = Number(this.year.value);
    const make = this.make.value;
    const model = this.model.value;
    const fromCatalog = new Set();
    (Array.isArray(this.engineCatalog) ? this.engineCatalog : []).forEach((row) => {
      if (!Array.isArray(row) || row.length < 5) return;
      const [rowMake, rowModel, start, end, list] = row;
      if (rowMake === make && rowModel === model && year >= start && year <= end && Array.isArray(list)) {
        list.forEach((engine) => fromCatalog.add(engine));
      }
    });
    if (fromCatalog.size) return [...fromCatalog];
    return [...new Set(this.matches().map((v) => v.engine).filter(Boolean))].sort(byText);
  }

  /** Every entry that fits the full selection, engine included. */
  results() {
    if (!this.year.value || !this.make.value || !this.model.value) return [];
    if (this.engine.required && !this.engine.value) return [];
    return this.matches().filter((v) => this.coversEngine(v, this.engine.value));
  }

  handleChange() {
    if (this.status) {
      this.status.hidden = true;
      this.status.textContent = '';
    }
    this.rebuild();
  }

  handleSubmit(event) {
    event.preventDefault();
    const complete = Boolean(this.year.value && this.make.value && this.model.value && (!this.engine.required || this.engine.value));
    const found = complete ? this.results() : [];
    if (!found.length) {
      if (this.status) {
        this.status.textContent = complete
          ? this.dataset.noMatchMessage || ''
          : this.dataset.incompleteMessage || '';
        this.status.hidden = false;
      }
      return;
    }
    const url = new URL(this.dataset.resultsUrl, window.location.origin);
    found.forEach((v) => url.searchParams.append('filter.p.m.custom.vehicles', v.id));
    window.location.assign(url.toString());
  }

  /**
   * Keyword mode (no verified vehicles yet): choosing a make narrows the model
   * and engine lists to that make, and submit joins the chosen words into one
   * product search. Once a model is picked the make word is left out, since
   * catalog titles often say "GM" rather than "Chevrolet".
   */
  setupKeywordMode(form) {
    const make = form.querySelector('[data-fitment-make]');
    const query = form.querySelector('[data-fitment-query]');
    const model = form.querySelector('[data-fitment-model]');
    const filterByMake = () => {
      form.querySelectorAll('option[data-make]').forEach((option) => {
        const show = !make.value || option.dataset.make === make.value;
        option.hidden = !show;
        option.disabled = !show;
        if (!show && option.selected) option.parentElement.value = '';
      });
    };
    make.addEventListener('change', filterByMake);
    filterByMake();
    form.addEventListener('submit', () => {
      const hasModel = Boolean(model.value);
      query.value = [...form.querySelectorAll('[data-fitment-field]')]
        .filter((field) => !(hasModel && field === make))
        .map((field) => field.value.trim())
        .filter(Boolean)
        .join(' ');
      make.removeAttribute('name');
    });
  }

  disconnectedCallback() {
    if (!this.form) return;
    this.form.removeEventListener('change', this.onChange);
    this.form.removeEventListener('submit', this.onSubmit);
  }
}

if (!customElements.get('s-fitment-finder')) {
  customElements.define('s-fitment-finder', FitmentFinder);
}
